import React, { useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { apiFetchJson } from '../../apiClient';
import {
  asArray,
  buildGoalProgress,
  normalizeGoalMap
} from './expenseInsightsUtils';

function ExpenseInsightsPanel({ period, insights, categories }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [goalMap, setGoalMap] = useState({});
  const [goalLoading, setGoalLoading] = useState(true);
  const [goalSavingId, setGoalSavingId] = useState(null);
  const [goalError, setGoalError] = useState('');

  useEffect(() => {
    const loadGoals = async () => {
      setGoalLoading(true);
      setGoalError('');
      try {
        const data = await apiFetchJson(instance, '/api/Expenses/goals');
        const goals = asArray(data);
        const loadedMap = goals.reduce((acc, goal) => {
          acc[goal.categoryId] = Number(goal.monthlyTarget) || 0;
          return acc;
        }, {});
        setGoalMap(normalizeGoalMap(loadedMap));
      } catch (err) {
        setGoalError(err.message || 'Failed to load goals');
      }
      setGoalLoading(false);
    };

    loadGoals();
  }, [instance]);

  const categoryChanges = asArray(insights?.categoryChanges);
  const recurringDrift = asArray(insights?.recurringDrift);
  const rollup = insights?.householdRollup;

  const topIncreases = categoryChanges
    .filter(item => (item.delta || 0) > 0)
    .slice(0, 3);

  const topDecreases = categoryChanges
    .filter(item => (item.delta || 0) < 0)
    .slice(0, 3);

  const goalProgress = useMemo(
    () => buildGoalProgress(categories, categoryChanges, goalMap),
    [categories, categoryChanges, goalMap]
  );

  const categoriesWithoutGoals = useMemo(() => {
    const categoryList = asArray(categories);
    return categoryList.filter(category => !goalMap[category.id]);
  }, [categories, goalMap]);

  const handleGoalEdit = (categoryId, value) => {
    setGoalMap(prev => ({
      ...prev,
      [categoryId]: value
    }));
  };

  const saveGoal = async (categoryId, candidateValue = null) => {
    const sourceValue = candidateValue ?? goalMap[categoryId];
    const next = normalizeGoalMap({ [categoryId]: sourceValue });
    const monthlyTarget = next[categoryId];
    if (!monthlyTarget) return;

    setGoalSavingId(categoryId);
    setGoalError('');
    try {
      await apiFetchJson(instance, `/api/Expenses/goals/${categoryId}`, {
        method: 'PUT',
        body: JSON.stringify({
          monthlyTarget,
          currency: 'NOK'
        })
      });
      setGoalMap(prev => normalizeGoalMap(prev));
    } catch (err) {
      setGoalError(err.message || 'Failed to save goal');
    }
    setGoalSavingId(null);
  };

  const addGoalForCategory = async (categoryId) => {
    setGoalMap(prev => ({
      ...prev,
      [categoryId]: 1000
    }));
    await saveGoal(categoryId, 1000);
  };

  const removeGoal = async (categoryId) => {
    setGoalSavingId(categoryId);
    setGoalError('');
    try {
      await apiFetchJson(instance, `/api/Expenses/goals/${categoryId}`, {
        method: 'DELETE'
      });
    } catch (err) {
      setGoalError(err.message || 'Failed to delete goal');
      setGoalSavingId(null);
      return;
    }

    const next = { ...goalMap };
    delete next[categoryId];
    setGoalMap(next);
    setGoalSavingId(null);
  };

  const periodLabel = new Date(period + '-01').toLocaleDateString('nb-NO', {
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="expense-insights-panel">
      <div className="expense-insights-header">
        <h3>📈 {t('expenses.insightsTitle')}</h3>
        <span>{periodLabel}</span>
      </div>

      {goalError && <p className="expense-error">⚠️ {goalError}</p>}

      <div className="expense-insights-grid">
        <section className="expense-insight-card">
          <h4>{t('expenses.whatChanged')}</h4>
          {topIncreases.length === 0 && topDecreases.length === 0 ? (
            <p className="expense-insight-empty">{t('expenses.noInsightsYet')}</p>
          ) : (
            <>
              {topIncreases.length > 0 && (
                <div>
                  <p className="expense-insight-subtitle">{t('expenses.increases')}</p>
                  {topIncreases.map(item => (
                    <div key={item.categoryId} className="expense-insight-row up">
                      <span>{item.icon} {item.categoryName}</span>
                      <strong>+{Number(item.delta).toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr</strong>
                    </div>
                  ))}
                </div>
              )}
              {topDecreases.length > 0 && (
                <div>
                  <p className="expense-insight-subtitle">{t('expenses.decreases')}</p>
                  {topDecreases.map(item => (
                    <div key={item.categoryId} className="expense-insight-row down">
                      <span>{item.icon} {item.categoryName}</span>
                      <strong>{Number(item.delta).toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr</strong>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>

        <section className="expense-insight-card">
          <h4>{t('expenses.recurringDrift')}</h4>
          {recurringDrift.length === 0 ? (
            <p className="expense-insight-empty">{t('expenses.noRecurringDrift')}</p>
          ) : (
            recurringDrift.slice(0, 5).map(item => (
              <div key={item.description} className="expense-insight-row drift">
                <span>{item.description}</span>
                <strong className={Number(item.delta) > 0 ? 'up' : 'down'}>
                  {Number(item.delta) > 0 ? '+' : ''}
                  {Number(item.delta).toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr
                </strong>
              </div>
            ))
          )}
        </section>

        <section className="expense-insight-card">
          <h4>{t('expenses.householdRollup')}</h4>
          {!rollup ? (
            <p className="expense-insight-empty">{t('expenses.noInsightsYet')}</p>
          ) : (
            <div className="expense-rollup-grid">
              <div>
                <span>{t('expenses.commonExpenses')}</span>
                <strong>{Number(rollup.commonTotal || 0).toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr</strong>
              </div>
              <div>
                <span>{t('expenses.commonPool')}</span>
                <strong>{Number(rollup.contributionsTotal || 0).toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr</strong>
              </div>
              <div>
                <span>{t('expenses.coverage')}</span>
                <strong>{Number(rollup.poolCoveragePercent || 0).toLocaleString('nb-NO', { minimumFractionDigits: 1 })}%</strong>
              </div>
              <div>
                <span>{t('expenses.remainingToSplit')}</span>
                <strong>{Number(rollup.remainingCommon || 0).toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr</strong>
              </div>
            </div>
          )}
        </section>
      </div>

      <section className="expense-goals-card">
        <h4>🎯 {t('expenses.goalBuckets')}</h4>
        {goalLoading && <p className="expense-insight-empty">{t('common.loading')}</p>}
        {!goalLoading && goalProgress.length === 0 ? (
          <p className="expense-insight-empty">{t('expenses.addGoalPrompt')}</p>
        ) : (
          <div className="expense-goal-list">
            {goalProgress.map(goal => (
              <div key={goal.categoryId} className="expense-goal-item">
                <div className="expense-goal-top">
                  <span>{goal.icon} {goal.categoryName}</span>
                  <button type="button" className="expense-btn-sm secondary" onClick={() => removeGoal(goal.categoryId)}>
                    {t('common.delete')}
                  </button>
                </div>
                <div className="expense-goal-input-row">
                  <label htmlFor={`goal-${goal.categoryId}`}>{t('expenses.target')}</label>
                  <input
                    id={`goal-${goal.categoryId}`}
                    type="number"
                    min="1"
                    value={goal.target}
                    onChange={(e) => handleGoalEdit(goal.categoryId, e.target.value)}
                  />
                  <button
                    type="button"
                    className="expense-btn-sm primary"
                    onClick={() => saveGoal(goal.categoryId)}
                    disabled={goalSavingId === goal.categoryId}
                  >
                    {goalSavingId === goal.categoryId ? t('common.saving') : t('common.save')}
                  </button>
                </div>
                <div className="expense-goal-metrics">
                  <span>{t('expenses.spent')}: {goal.spent.toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr</span>
                  <span className={goal.overTarget ? 'down' : 'up'}>
                    {goal.overTarget ? t('expenses.overBy') : t('expenses.left')}:
                    {' '}
                    {Math.abs(goal.remaining).toLocaleString('nb-NO', { minimumFractionDigits: 0 })} kr
                  </span>
                </div>
                <div className="expense-goal-bar">
                  <div
                    className={`expense-goal-fill ${goal.overTarget ? 'over' : ''}`}
                    style={{ width: `${Math.min(goal.ratio, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {categoriesWithoutGoals.length > 0 && (
          <div className="expense-goal-add">
            <p>{t('expenses.addGoalForCategory')}</p>
            <div className="expense-goal-add-list">
              {categoriesWithoutGoals.slice(0, 8).map(category => (
                <button
                  key={category.id}
                  type="button"
                  className="expense-btn-sm primary"
                  onClick={() => addGoalForCategory(category.id)}
                >
                  + {category.icon} {category.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default ExpenseInsightsPanel;
